
import type { WidgetHandle } from './main'

const MANIFEST_NAME = 'braini-widget.manifest.js'
const MANIFEST_GLOBAL = '__BRAINI_WIDGET_MANIFEST__'
const LOADER_FILE = 'braini-widget.loader.js'

const SCRIPT_TIMEOUT_MS = 15000
const MAX_RETRIES = 3
const LOADER_TAG_ATTR = 'data-braini-loader'

interface Manifest {
  main: string
  builtAt?: string
}

type QueuedCall =
  | { kind: 'mount'; selector: string; site: string; handle: DeferredHandle }
  | { kind: 'unmount'; selector: string }

class DeferredHandle implements WidgetHandle {
  private unmountRequested = false
  private real: WidgetHandle | null = null
  private failed = false

  unmount(): void {
    if (this.real) {
      this.real.unmount()
    } else {
      this.unmountRequested = true
    }
  }

  resolve(real: WidgetHandle): void {
    this.real = real
    if (this.unmountRequested) {
      real.unmount()
    }
  }

  reject(): void {
    this.failed = true
    this.unmountRequested = false
  }

  get isFailed(): boolean {
    return this.failed
  }
}

interface RealWidgetApi {
  mountBrainiWidget(selector: string, site: string): WidgetHandle
  unmountBrainiWidget(selector: string): void
}

interface BrainiWidgetGlobal extends Partial<RealWidgetApi> {
  __isLoaderStub?: boolean
  __queue?: QueuedCall[]
}

declare global {
  interface Window {
    BrainiWidget?: BrainiWidgetGlobal
    __BRAINI_WIDGET_MANIFEST__?: Manifest
  }
}

function resolveBaseUrl(): string {
  const scripts = Array.from(document.getElementsByTagName('script'))
  const loaderScript = scripts.find((s) => s.src && s.src.indexOf(LOADER_FILE) !== -1)

  let src = loaderScript?.src

  if (!src && document.currentScript instanceof HTMLScriptElement) {
    src = document.currentScript.src
  }

  if (!src) {
    return ''
  }

  const idx = src.lastIndexOf('/')
  return idx === -1 ? '' : src.slice(0, idx + 1)
}

const BASE_URL = resolveBaseUrl()

let loadStarted = false
let retryCount = 0

function ensureStub(): BrainiWidgetGlobal {
  const existing = window.BrainiWidget

  if (existing) {
    return existing
  }

  const queue: QueuedCall[] = []

  const stub: BrainiWidgetGlobal = {
    __isLoaderStub: true,
    __queue: queue,
    mountBrainiWidget(selector: string, site: string): WidgetHandle {
      const handle = new DeferredHandle()
      queue.push({ kind: 'mount', selector, site, handle })
      void startLoading()
      return handle
    },
    unmountBrainiWidget(selector: string): void {
      queue.push({ kind: 'unmount', selector })
      void startLoading()
    }
  }

  window.BrainiWidget = stub
  return stub
}

function removeLoaderTag(marker: string): void {
  const selector = `script[${LOADER_TAG_ATTR}="${marker}"]`
  const nodes = document.querySelectorAll(selector)
  nodes.forEach((node) => node.parentNode?.removeChild(node))
}

// Удаляем прежний тег с тем же маркером перед повторной попыткой загрузки.
function loadScript(url: string, marker: string): Promise<void> {
  removeLoaderTag(marker)

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = url
    script.async = true
    script.setAttribute(LOADER_TAG_ATTR, marker)

    let settled = false

    const cleanup = () => {
      clearTimeout(timer)
      script.onload = null
      script.onerror = null
    }

    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      cleanup()
      script.parentNode?.removeChild(script)
      reject(new Error(`Таймаут загрузки скрипта (${SCRIPT_TIMEOUT_MS} мс): ${url}`))
    }, SCRIPT_TIMEOUT_MS)

    script.onload = () => {
      if (settled) return
      settled = true
      cleanup()
      resolve()
    }

    script.onerror = () => {
      if (settled) return
      settled = true
      cleanup()
      script.parentNode?.removeChild(script)
      reject(new Error(`Не удалось загрузить скрипт: ${url}`))
    }

    document.head.appendChild(script)
  })
}

async function fetchManifest(): Promise<Manifest> {
  const url = `${BASE_URL}${MANIFEST_NAME}?t=${Date.now()}`
  await loadScript(url, 'manifest')

  const data = window[MANIFEST_GLOBAL as '__BRAINI_WIDGET_MANIFEST__']

  if (!data || typeof data.main !== 'string' || !data.main) {
    throw new Error('Некорректный манифест: отсутствует поле "main"')
  }

  return data
}

function injectBundle(fileName: string): Promise<void> {
  return loadScript(`${BASE_URL}${fileName}`, 'bundle')
}

function drainQueue(api: RealWidgetApi, queue: QueuedCall[]): void {
  const calls = queue.splice(0, queue.length)

  for (const call of calls) {
    try {
      if (call.kind === 'mount') {
        const realHandle = api.mountBrainiWidget(call.selector, call.site)
        call.handle.resolve(realHandle)
      } else {
        api.unmountBrainiWidget(call.selector)
      }
    } catch (error) {
      console.error('BrainiWidget: ошибка при выполнении отложенного вызова', error)
      if (call.kind === 'mount') {
        call.handle.reject()
      }
    }
  }
}

async function startLoading(): Promise<void> {
  if (loadStarted) return
  loadStarted = true

  const stub = ensureStub()

  try {
    const manifest = await fetchManifest()
    await injectBundle(manifest.main)

    const api = window.BrainiWidget

    if (!api || api.__isLoaderStub || typeof api.mountBrainiWidget !== 'function') {
      throw new Error('Бандл загружен, но не предоставил реальный API BrainiWidget')
    }

    removeLoaderTag('manifest')

    retryCount = 0

    const queue = stub.__queue ?? []
    drainQueue(api as RealWidgetApi, queue)
  } catch (error) {
    console.error('BrainiWidget Loader: не удалось инициализировать виджет', error)

    removeLoaderTag('manifest')
    removeLoaderTag('bundle')

    if (retryCount < MAX_RETRIES) {
      retryCount += 1
      loadStarted = false
      console.warn(
        `BrainiWidget Loader: повторная попытка ${retryCount}/${MAX_RETRIES} будет выполнена при следующем вызове`
      )
    } else {
      console.error(
        `BrainiWidget Loader: исчерпан лимит попыток (${MAX_RETRIES}). Инициализация остановлена.`
      )
    }
  }
}

ensureStub()

export {}
